// Centralized auth utilities
// Provides a single handler for 401 Unauthorized responses so logic is consistent
// across services (logout, redirect with preserved path & query, session_expired flag).

import { useAuthStore } from '@/stores/auth';

// Prevent re-entrant multiple rapid auth redirects
let handling = false; 
let lastHandleTime = 0;
let consecutiveFailures = 0;

export async function handleResponse(
  response: Response,
  messages: { unauthorized?: string } = {}
): Promise<void> {
  if (response.status === 401) {
    const now = Date.now();
    
    // Enhanced rate limiting with progressive backoff
    const timeSinceLastHandle = now - lastHandleTime;
    const minBackoffTime = Math.min(3000 + (consecutiveFailures * 1000), 10000); // 3s to 10s max
    
    // Prevent rapid successive 401 handling
    if (handling || timeSinceLastHandle < minBackoffTime) {
      console.debug(`Skipping 401 handling - too recent (${timeSinceLastHandle}ms < ${minBackoffTime}ms)`);
      return;
    }
    
    // Check if this might be a legitimate 401 vs a false positive
    const authError = response.headers.get('X-Auth-Error');
    const isAuthRequired = authError === 'authentication_required';
    
    console.warn('Handling 401 Unauthorized response:', {
      url: response.url,
      authError,
      isAuthRequired,
      consecutiveFailures,
      timeSinceLastHandle
    });
    
    handling = true;
    lastHandleTime = now;
    
    try {
      const authStore = useAuthStore();
      
      // Double-check if we're actually authenticated before logging out
      if (!authStore.isAuthenticated) {
        console.debug('Already not authenticated, skipping logout');
        return;
      }
      
      // Try to refresh token first (only if we have refresh token)
      if (authStore.refreshToken && authStore.shouldRefreshToken()) {
        console.debug('Attempting token refresh before logout');
        const refreshed = await authStore.refreshAccessToken();
        
        if (refreshed) {
          console.log('Token refreshed successfully, avoiding logout');
          consecutiveFailures = 0; // Reset on successful refresh
          return;
        } else {
          console.warn('Token refresh failed, proceeding with logout');
        }
      }
      
      consecutiveFailures++;
      console.log('Logging out due to 401 response');
      await authStore.logout();
      
      // Get current path but ensure it's valid
      let redirectPath = window.location.pathname + window.location.search;
      
      // Don't redirect to problematic paths
      if (redirectPath.includes('/src') || 
          redirectPath.includes('/assets') || 
          redirectPath.startsWith('/auth/') ||
          redirectPath.includes('undefined')) {
        redirectPath = '/';
      }
      
      const errorParam = isAuthRequired ? 'authentication_required' : 'session_expired';
      const loginUrl = `/login?error=${errorParam}&redirect=${encodeURIComponent(redirectPath)}`;
      
      console.log('Redirecting to login:', loginUrl);
      window.location.replace(loginUrl);
      
      throw new Error(messages.unauthorized || 'Authentication required');
    } catch (error) {
      console.error('Error during 401 handling:', error);
      throw error;
    } finally {
      // Reset handling flag with longer delay, but reset consecutive failures on success
      setTimeout(() => { 
        handling = false;
        // Only reset consecutive failures if enough time has passed successfully
        if (consecutiveFailures > 0 && Date.now() - lastHandleTime > 60000) { // 1 minute
          consecutiveFailures = Math.max(0, consecutiveFailures - 1);
        }
      }, minBackoffTime);
    }
  } else {
    // Reset consecutive failures on successful response
    if (consecutiveFailures > 0) {
      consecutiveFailures = 0;
    }
  }
  // Future: handle 403 (forbidden), 419 (session timeout variants), etc.
}

export const handleUnauthorized = async (reason?: string) => {
  const fake = new Response(null, { status: 401 });
  await handleResponse(fake, { unauthorized: reason });
};
